import { format } from 'date-fns'

import {
  ACTION,
  ALLOCATION,
  ALLOCATION_UNIT,
  BID_CAP,
  BRAND_KEYWORDS,
  BUDGET,
  CAMPAIGNS,
  CREATIVE_ASSETS_DATE,
  DELIVERED,
  DIGITAL_ALLOCATION_IMPRESSIONS,
  DIGITAL_ALLOCATION_TYPE,
  DIGITAL_ALLOCATION_TYPE_BUDGET,
  DIGITAL_ALLOCATION_TYPE_IMPRESSIONS,
  INCLUDE_LINKED_INSTAGRAM_ACCOUNT,
  LINKED_INSTAGRAM_ACCOUNT,
  LOGO_DATE,
  NAME,
  REQUEST_CREATIVE_ASSETS,
  REQUEST_LOGO,
  RIGHT_TYPE,
  RIGHT_TYPE_DIGITAL,
  RIGHT_TYPE_ONSITE,
  RIGHT_TYPE_OTHER,
  RIGHT_TYPE_POSTS,
  SOCIAL_MEDIA_POST,
  STATUS
} from '../../fields'
import { DATES_FORMAT_BE } from '../../../../../../constants/dates'
import { END_DATE, START_DATE } from '../../../../../ReusableFormFields/StartEndDateRangeFields/fields'
import { FACEBOOK_PROVIDER_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import {
  AD_INVENTORY,
  AD_INVENTORY_TARGETING_ENABLED,
  ADVANCED_SETTINGS,
  ALLOCATION_TYPE,
  allocationTypes,
  BILLING_EVENT,
  IS_SUB_ALLOCATION,
  CUSTOM_TARGETING_ENABLED,
  SKIPPABLE_ADS_ENABLED,
  SUB_ALLOCATION,
  TOTAL_VALUE,
  GEO_TARGETING_ENABLED,
  DEVICE_TARGETING_ENABLED
} from './sectionsLists/OnsiteRightCreateSections/fields'

import {
  calculateTotalRightValue,
  getAllocationInfo,
  getBillingEventType,
  getShowTotalRightValue
} from './sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/helpers'
import { formatAdInventoryToBE, transformSubAllocationsToBE } from './gamFormatters'

const transformAllocationToBE = (values, gamNetworkCode) => {
  switch (values[RIGHT_TYPE]) {
    case RIGHT_TYPE_DIGITAL:
      return {
        allocation_type: values[DIGITAL_ALLOCATION_TYPE],
        ...(values[DIGITAL_ALLOCATION_TYPE] === DIGITAL_ALLOCATION_TYPE_BUDGET && { [BUDGET]: values[BUDGET] }),
        ...(values[DIGITAL_ALLOCATION_TYPE] === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS && {
          [ALLOCATION]: values[DIGITAL_ALLOCATION_IMPRESSIONS],
          [BID_CAP]: values[BID_CAP]
        })
      }
    case RIGHT_TYPE_ONSITE:
      if (gamNetworkCode) {
        const { allocationGroup } = getAllocationInfo(values)

        return {
          ...(!values[IS_SUB_ALLOCATION]
            ? {
                [values[ALLOCATION_TYPE] === allocationTypes.budget
                  ? // in case user selected budget allocation type we need to send allocation into budget field
                    BUDGET
                  : ALLOCATION]: values[allocationGroup][ALLOCATION]
              }
            : {})
        }
      } else {
        return {
          [ALLOCATION]: values[ALLOCATION],
          [ALLOCATION_UNIT]: values[ALLOCATION_UNIT]
        }
      }

    case RIGHT_TYPE_OTHER:
    case RIGHT_TYPE_POSTS:
    default:
      return {
        [ALLOCATION]: values[ALLOCATION],
        [ALLOCATION_UNIT]: values[ALLOCATION_UNIT],
        ...(!!values[DELIVERED] && { [DELIVERED]: values[DELIVERED] })
      }
  }
}

export const generalRightPayloadToBE = (values, gamNetworkCode) => {
  const allocationPayload = transformAllocationToBE(values, gamNetworkCode)

  return {
    [NAME]: values[NAME],
    [STATUS]: values[STATUS],
    ...allocationPayload,
    // optional fields
    ...(!!values[START_DATE] && { start_date: format(new Date(values[START_DATE]), DATES_FORMAT_BE) }),
    ...(!!values[END_DATE] && { end_date: format(new Date(values[END_DATE]), DATES_FORMAT_BE) })
  }
}

const transformOnsiteRightToBE = ({ values, rightsHolderAdServer, reusablePayload, gamNetworkCode }) => {
  const showTotalRightValue = getShowTotalRightValue(values[ALLOCATION_TYPE])

  if (gamNetworkCode) {
    const { isPercentageAllocation } = getAllocationInfo(values)

    return {
      ...reusablePayload,
      [ALLOCATION_TYPE]: values[ALLOCATION_TYPE],
      [IS_SUB_ALLOCATION]: values[IS_SUB_ALLOCATION],
      [AD_INVENTORY]: formatAdInventoryToBE(values, gamNetworkCode),
      ...(values[IS_SUB_ALLOCATION] && {
        [SUB_ALLOCATION]: transformSubAllocationsToBE(values)
      }),
      [ADVANCED_SETTINGS]: {
        [SKIPPABLE_ADS_ENABLED]: values[ADVANCED_SETTINGS]?.[SKIPPABLE_ADS_ENABLED],
        [AD_INVENTORY_TARGETING_ENABLED]: values[IS_SUB_ALLOCATION]
          ? // for sub allocation we don't show ad inventory targeting toggle
            true
          : values[ADVANCED_SETTINGS]?.[AD_INVENTORY_TARGETING_ENABLED],

        ...(isPercentageAllocation
          ? // For any sponsorship right do not show any of Geo-targeting,Device & browser targeting or Custom targeting.
            // Automatically disable all of them in the right
            {
              [GEO_TARGETING_ENABLED]: false,
              [DEVICE_TARGETING_ENABLED]: false,
              [CUSTOM_TARGETING_ENABLED]: false
            }
          : {
              [GEO_TARGETING_ENABLED]: values[ADVANCED_SETTINGS]?.[GEO_TARGETING_ENABLED],
              [DEVICE_TARGETING_ENABLED]: values[ADVANCED_SETTINGS]?.[DEVICE_TARGETING_ENABLED],
              [CUSTOM_TARGETING_ENABLED]: values[ADVANCED_SETTINGS]?.[CUSTOM_TARGETING_ENABLED]
            })
      },
      [TOTAL_VALUE]: values[TOTAL_VALUE],
      ...(showTotalRightValue && {
        [TOTAL_VALUE]: calculateTotalRightValue(values)
      }),
      [BILLING_EVENT]: getBillingEventType(values[ALLOCATION_TYPE], values[BILLING_EVENT])
    }
  } else {
    const filledCampaigns = values[CAMPAIGNS].filter(item => !!item.text)

    const formattedCampaigns = filledCampaigns.map(campaign => ({
      external_id: campaign.text,
      provider: rightsHolderAdServer
    }))

    return {
      ...reusablePayload,
      [ALLOCATION_UNIT]: 2, // 2 === impressions
      ...(filledCampaigns.length && { [CAMPAIGNS]: formattedCampaigns })
    }
  }
}

// todo reuse for edit form if types approach will be applied there
export const transformValuesToBE = ({ values, rightsHolderAdServer, gamNetworkCode }) => {
  const generalPayload = generalRightPayloadToBE(values, gamNetworkCode)

  const includeCreativeAssetsDate = values[REQUEST_CREATIVE_ASSETS] && values[CREATIVE_ASSETS_DATE]
  const includeLogoDate = values[REQUEST_LOGO] && values[LOGO_DATE]

  const reusablePayload = {
    ...generalPayload,
    [STATUS]: values[STATUS],
    [RIGHT_TYPE]: values[RIGHT_TYPE],
    [REQUEST_CREATIVE_ASSETS]: values[REQUEST_CREATIVE_ASSETS],
    ...(includeCreativeAssetsDate && {
      [CREATIVE_ASSETS_DATE]: format(new Date(values[CREATIVE_ASSETS_DATE]), DATES_FORMAT_BE)
    }),
    [REQUEST_LOGO]: values[REQUEST_LOGO],
    ...(includeLogoDate && { [LOGO_DATE]: format(new Date(values[LOGO_DATE]), DATES_FORMAT_BE) }),
    [ACTION]: values[ACTION]
  }

  switch (values[RIGHT_TYPE]) {
    case RIGHT_TYPE_OTHER:
      return {
        ...reusablePayload
      }
    case RIGHT_TYPE_DIGITAL:
      return reusablePayload
    case RIGHT_TYPE_POSTS:
      const filledKeywords = values[BRAND_KEYWORDS].map(item => item.text).filter(item => !!item)
      const includeInstagramAccount = values[INCLUDE_LINKED_INSTAGRAM_ACCOUNT]
      const instagramAccount = values[LINKED_INSTAGRAM_ACCOUNT]

      return {
        ...reusablePayload,
        [ALLOCATION_UNIT]: 7, // 7 === posts
        ...(filledKeywords.length && { [BRAND_KEYWORDS]: filledKeywords }),
        ...(values[SOCIAL_MEDIA_POST] && {
          pages: [
            {
              // For now provider is always hardcoded to 'facebook'
              provider: FACEBOOK_PROVIDER_PLATFORM,
              page_id: values[SOCIAL_MEDIA_POST]
            }
          ]
        }),
        ...(includeInstagramAccount &&
          instagramAccount && {
            instagram_accounts: [instagramAccount]
          })
      }
    case RIGHT_TYPE_ONSITE:
      return transformOnsiteRightToBE({
        values,
        rightsHolderAdServer,
        reusablePayload,
        gamNetworkCode
      })
    default:
      return reusablePayload
  }
}
